import { useCallback, useEffect, useState, createContext, ReactNode, useContext, useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithStorage, RESET } from 'jotai/utils';

import { UTMSourceType } from '@restworld/data-services';

import { useNavigationContext } from './navigation-context';

const UTMSourceContext = createContext<{
  utmSource: UTMSourceType;
  refCode: string | null;
  partnerCode: string | null;
  removeRefCode: () => void;
  removeUtmSource: () => void;
  removePartnerCode: () => void;
}>({
  utmSource: null,
  refCode: null,
  partnerCode: null,
  removeRefCode: () => null,
  removeUtmSource: () => null,
  removePartnerCode: () => null,
});
const UTM_SOURCE_STORAGE_KEY = 'utm_source';
const PARTNER_CODE_STORAGE_KEY = 'partner_code';

/**
 * `localStorage` only when it is actually usable, otherwise `null`.
 *
 * An Android WebView with site data disabled exposes `window.localStorage` as **null**, and
 * `typeof null === 'object'` defeats the usual `typeof … !== 'undefined'` probe. `saveReferralCode`
 * runs from an effect inside this provider, which sits inside `MainContext`, and React routes an
 * error thrown in an effect to the nearest ErrorBoundary — so an unguarded read here takes the whole
 * page down exactly like the `getItem` crash of 02/10/2026 did one provider higher up.
 */
const usableLocalStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

const utmSourceAtom = atomWithStorage<UTMSourceType>(UTM_SOURCE_STORAGE_KEY, null);
const partnerCodeAtom = atomWithStorage<string | null>(PARTNER_CODE_STORAGE_KEY, null);

const UTMSourceProvider = ({ children }: { children: ReactNode }) => {
  const [utmSource, setUtmSource] = useAtom(utmSourceAtom);
  const [partnerCode, setPartnerCode] = useAtom(partnerCodeAtom);
  const [refCode, setRefCode] = useState<string | null>(null);
  const { router } = useNavigationContext();
  const query = router?.['query'];
  const queryRefCode = query?.ref_code;
  const queryUtmSource = query?.utm_source;
  const queryPartner = query?.partner;
  const events = router?.events;
  const isRouteReady = router?.isReady;
  const isQueryUTMSourceValid = useMemo(() => typeof queryUtmSource === 'string' && !!queryUtmSource, [queryUtmSource]);
  const isQueryPartnerValid = useMemo(() => typeof queryPartner === 'string' && !!queryPartner, [queryPartner]);

  const saveUTMSource = useCallback(() => {
    if (!isQueryUTMSourceValid) return;
    if (queryUtmSource) setUtmSource(queryUtmSource);
  }, [isQueryUTMSourceValid, queryUtmSource, setUtmSource]);

  const savePartnerCode = useCallback(() => {
    if (!isQueryPartnerValid) return;
    if (typeof queryPartner === 'string') setPartnerCode(queryPartner);
  }, [isQueryPartnerValid, queryPartner, setPartnerCode]);

  const saveReferralCode = useCallback(() => {
    const urlRefCode = queryRefCode && Array.isArray(queryRefCode) ? queryRefCode[0] : queryRefCode;
    const storage = usableLocalStorage();
    const localstorageRefCode = storage?.getItem('ref_code') ?? null;

    if (urlRefCode) {
      try {
        storage?.setItem('ref_code', urlRefCode);
      } catch {
        // A store that exists but refuses writes. The code still applies to this page view.
      }
      setRefCode(urlRefCode);
    } else if (localstorageRefCode) {
      setRefCode(localstorageRefCode);
    }
  }, [queryRefCode]);

  useEffect(() => {
    if (!events) return;
    events.on('routeChangeStart', saveUTMSource);
    events.on('routeChangeStart', savePartnerCode);
    return () => {
      events.off('routeChangeStart', saveUTMSource);
      events.off('routeChangeStart', savePartnerCode);
    };
  }, [events, saveUTMSource, savePartnerCode]);

  useEffect(() => {
    if (isRouteReady) {
      saveUTMSource();
      savePartnerCode();
      saveReferralCode();
    }
  }, [isRouteReady, saveUTMSource, savePartnerCode, saveReferralCode]);

  const removeRefCode = useCallback(() => {
    try {
      usableLocalStorage()?.removeItem('ref_code');
    } catch {
      // Nothing to clear if the store will not cooperate; the in-memory value below is what matters.
    }
    setRefCode(null);
  }, []);

  const removeUtmSource = useCallback(() => {
    setUtmSource(RESET);
  }, [setUtmSource]);

  const removePartnerCode = useCallback(() => {
    setPartnerCode(RESET);
  }, [setPartnerCode]);

  return (
    <UTMSourceContext.Provider
      value={{ utmSource, refCode, partnerCode, removeRefCode, removeUtmSource, removePartnerCode }}
    >
      {children}
    </UTMSourceContext.Provider>
  );
};

const useUTMSource = () => {
  const context = useContext(UTMSourceContext);
  if (!context) throw new Error('useUTMSource must be used within a UTMSourceContext');
  return useMemo(() => context, [context]);
};

export { UTMSourceProvider, useUTMSource };
